import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface Referral {
  id: string;
  referrer_id: string;
  referred_user_id: string | null;
  referral_code: string;
  created_at: string;
  used_at: string | null;
  status: 'pending' | 'completed' | 'expired';
}

export function useReferrals() {
  const queryClient = useQueryClient();

  // Generate a new referral code for a user
  const generateReferralCode = async (userId: string): Promise<string> => {
    const { data, error } = await supabase.rpc('create_user_referral', {
      p_user_id: userId
    });

    if (error) {
      console.error('Error generating referral code:', error);
      throw new Error(error.message);
    }

    return data as string;
  };

  // Get all referrals for a user (as referrer)
  const fetchUserReferrals = async (userId: string): Promise<Referral[]> => {
    const { data, error } = await supabase
      .from('user_referrals')
      .select('*')
      .eq('referrer_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching referrals:', error);
      throw new Error(error.message);
    }

    return data as Referral[];
  };

  // Get referral by code (for registration)
  const fetchReferralByCode = async (code: string): Promise<Referral | null> => {
    const { data, error } = await supabase
      .from('user_referrals')
      .select('*')
      .eq('referral_code', code)
      .eq('status', 'pending')
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null;
      }
      console.error('Error fetching referral by code:', error);
      throw new Error(error.message);
    }

    return data as Referral;
  };

  // Mark referral as used
  const useReferralCode = async (code: string, referredUserId: string): Promise<boolean> => {
    const { data, error } = await supabase.rpc('use_referral_code', {
      p_code: code,
      p_referred_user_id: referredUserId
    });

    if (error) {
      console.error('Error using referral code:', error);
      throw new Error(error.message);
    }

    return data as boolean;
  };

  // Query to get user's referrals
  const useUserReferralsQuery = (userId?: string) => {
    return useQuery({
      queryKey: ['userReferrals', userId],
      queryFn: () => fetchUserReferrals(userId!),
      enabled: !!userId,
    });
  };

  // Mutation to generate referral code
  const useGenerateReferralMutation = () => {
    return useMutation({
      mutationFn: generateReferralCode,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['userReferrals'] });
        toast({
          title: "Success",
          description: "Invitation link generated successfully",
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: `Failed to generate invitation link: ${error.message}`,
          variant: "destructive",
        });
      },
    });
  };

  // Mutation to use referral code
  const useReferralCodeMutation = () => {
    return useMutation({
      mutationFn: ({ code, userId }: { code: string; userId: string }) =>
        useReferralCode(code, userId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['userReferrals'] });
      },
      onError: (error: Error) => {
        console.error('Error using referral code:', error);
      },
    });
  };

  return {
    useUserReferralsQuery,
    useGenerateReferralMutation,
    useReferralCodeMutation,
    fetchReferralByCode,
    generateReferralCode,
  };
}
